import { supabase } from '@/lib/supabase';

export type ResolvedPaperPdf = {
  allowed: boolean;
  reason: 'ok' | 'forbidden' | 'not_found' | 'version_not_found' | string;
  storage_path: string | null;
  external_url: string | null;
  filename: string;
  paper_visibility: 'public' | 'private' | null;
  version_id: string | null;
  version: string | null;
};

/**
 * Server-side access check for a paper PDF. Returns the storage path (for
 * signed-URL minting) or an external URL for arXiv/external papers.
 */
export async function resolvePaperPdf(
  paperId: string,
  versionId?: string | null,
): Promise<ResolvedPaperPdf | null> {
  const { data, error } = await supabase.rpc('resolve_paper_pdf', {
    p_paper_id: paperId,
    p_version_id: versionId ?? null,
  });

  if (error) {
    console.error('resolve_paper_pdf failed:', error);
    return null;
  }
  if (!data) return null;

  const d = data as Record<string, unknown>;
  return {
    allowed: Boolean(d.allowed),
    reason: (d.reason as string) ?? 'unknown',
    storage_path: (d.storage_path as string) ?? null,
    external_url: (d.external_url as string) ?? null,
    filename: (d.filename as string) ?? 'paper.pdf',
    paper_visibility: (d.paper_visibility as 'public' | 'private') ?? null,
    version_id: (d.version_id as string) ?? null,
    version: (d.version as string) ?? null,
  };
}

/**
 * Mints a short-lived signed URL for a papers-bucket object. Requires the
 * caller's session to have SELECT on the object per storage RLS.
 */
export async function createPaperPdfSignedUrl(
  storagePath: string,
  filename: string | null,
  ttlSeconds = 600,
): Promise<string | null> {
  const options = filename ? { download: filename } : undefined;
  const { data, error } = await supabase.storage
    .from('papers')
    .createSignedUrl(storagePath, ttlSeconds, options);
  if (error || !data?.signedUrl) {
    console.error('createSignedUrl failed:', error);
    return null;
  }
  return data.signedUrl;
}

/**
 * The bookmarkable app route for a paper's PDF.
 */
export function paperPdfRoute(paperId: string, versionId?: string | null): string {
  return versionId ? `/paper/${paperId}/v/${versionId}/pdf` : `/paper/${paperId}/pdf`;
}
